import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, MoreVertical } from 'lucide-react';
import DropdownMenu from './DropdownMenu';
import { findSidebarCategoryForNavId } from '../../utils/fileContentStructureUtils';

/* ------------------------- Generic Sidebar Item ------------------------- */
const SidebarItem = ({
    id,
    name,
    activeSection,
    handleNavigate,
    openDropdown,
    toggleDropdown,
    isLastItem,
    canDelete,
    onDelete,
    onRename,
    onMove,
    onPreview,
    onSampleContent,
    isSubsection = false,
    canMoveUp = true,
    canMoveDown = true
}) => {
    // Safety check
    if (!id) return null;

    return (
        <div
            data-nav-id={id}
            role="button"
            tabIndex={0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => id && handleNavigate(id)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    id && handleNavigate(id);
                }
            }}
            className={`flex items-center justify-between w-full text-base text-left transition-colors relative cursor-pointer ${activeSection === id ? 'text-purple bg-purple/10' : 'text-gray hover:text-dark-blue'
                }`}
        >
            <div className="flex items-center gap-2 text-base min-w-0 flex-1 text-left">
                <span className="truncate block text-left">{name}</span>
            </div>

            {activeSection === id && (
                <div className="relative shrink-0 ml-2">
                    <MoreVertical
                        className="w-4 h-4 text-gray cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(id);
                        }}
                    />
                    <DropdownMenu
                        itemId={id}
                        isOpen={openDropdown === id}
                        onClose={() => toggleDropdown(null)}
                        isLastItem={isLastItem}
                        canDelete={canDelete}
                        onDelete={() => onDelete(id)}
                        onRename={() => onRename(id)}
                        onMove={(itemId, direction) => onMove(itemId, direction)}
                        onPreview={() => onPreview(id)}
                        onSampleContent={() => onSampleContent(id)}
                        isSubsection={isSubsection} // Pass this prop down
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                    />
                </div>
            )}
        </div>
    );
};

/* -------------------- Optional Section Item -------------------- */
const OptionalSectionItem = ({ id, name, onAdd, isRestore = false }) => {
    if (!id) return null;
    const label = isRestore ? `Restore "${name}"` : `Add "${name}"`;
    const activate = () => onAdd(id);
    return (
        <div
            role="button"
            tabIndex={0}
            title={label}
            onClick={activate}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    activate();
                }
            }}
            className="flex items-center justify-between w-full text-base text-left py-1 px-2 rounded transition-colors text-gray hover:text-dark-blue hover:bg-gray-50 cursor-pointer"
        >
            <div className="flex items-center gap-2 text-base min-w-0 flex-1 text-left pointer-events-none">
                <span className="truncate block text-left opacity-70">{name}</span>
            </div>
            <span className="shrink-0 ml-2 p-1 rounded hover:bg-purple/10 transition-colors pointer-events-none" aria-hidden>
                <Plus className={`w-4 h-4 ${isRestore ? 'text-red-500' : 'text-purple'}`} />
            </span>
        </div>
    );
};

/* --------------------- Main EditorSidebarSection ---------------------- */
export default function EditorSidebarSection({
    sections,
    onNavigate,
    onMoveSection,
    onMoveSubsection,
    onAddSection,
    onDeleteSection,
    onAddSubsection,
    onDeleteSubsection,
    onPreviewSection,
    onSampleContent,
    canUseTemplates = true,
    syncActiveSectionId = null
}) {
    const [openSections, setOpenSections] = useState({});
    const [activeSection, setActiveSection] = useState(null);
    const [openDropdown, setOpenDropdown] = useState(null);
    const sidebarScrollRef = useRef(null);
    const lastScrolledNavIdRef = useRef(null);

    const toggleSection = (key) => {
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
        if (openSections[key]) {
            setOpenDropdown(null);
            setActiveSection(null);
        }
    };

    const handleNavigate = (id) => {
        setActiveSection(id);
        onNavigate(id);
    };

    const toggleDropdown = (id) => {
        setOpenDropdown(openDropdown === id ? null : id);
    };

    const isCategories =
        Array.isArray(sections) &&
        sections.length > 0 &&
        sections[0].name !== undefined &&
        sections[0].sections !== undefined;

    // Auto-expand a single outline group (e.g. flat sections wrapped as one category)
    useEffect(() => {
        if (!isCategories || sections.length !== 1 || !sections[0]?.name) return;
        setOpenSections((prev) =>
            prev[sections[0].name] ? prev : { ...prev, [sections[0].name]: true }
        );
    }, [sections, isCategories]);

    // Keep highlight in sync with editor cursor / scroll position (M3-123)
    useEffect(() => {
        if (!syncActiveSectionId) return;

        setActiveSection(syncActiveSectionId);

        const categoryName = findSidebarCategoryForNavId(sections, syncActiveSectionId);
        if (categoryName) {
            setOpenSections((prev) =>
                prev[categoryName] ? prev : { ...prev, [categoryName]: true }
            );
        }

        if (lastScrolledNavIdRef.current === syncActiveSectionId) return;
        lastScrolledNavIdRef.current = syncActiveSectionId;

        requestAnimationFrame(() => {
            const root = sidebarScrollRef.current;
            if (!root) return;
            const item = root.querySelector(`[data-nav-id="${CSS.escape(syncActiveSectionId)}"]`);
            if (item) {
                item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        });
    }, [syncActiveSectionId, sections]);

    if (!sections || !Array.isArray(sections)) return null;

    return (
        <div ref={sidebarScrollRef} className="w-full bg-white sticky top-0 h-screen overflow-y-auto">
            {isCategories
                ? // New category-based structure
                sections.map((category, categoryIdx) => {
                    if (!category) return null; // Safety check
                    const isOpen = openSections[category.name];

                    return (
                        <div
                            key={category.id || categoryIdx}
                            className={`flex flex-col w-full border-b border-lightGrey px-[25px] ${isOpen ? 'gap-[30px] py-[18px]' : 'py-[18px]'}`}
                        >
                            {/* Category Header */}
                            <button
                                onClick={() => toggleSection(category.name)}
                                className="w-full flex justify-between items-center text-dark-blue font-semibold text-20 text-left"
                            >
                                <div className="flex items-center gap-2 text-left min-w-0 flex-1">
                                    <span className="truncate w-full block">{category.name}</span>
                                </div>
                                <div className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                                    <ChevronDown className="w-4 h-4" />
                                </div>
                            </button>

                            {/* Expanded Content */}
                            {isOpen && (
                                <div className="flex flex-col items-start gap-2 w-full max-w-full">
                                    
                                    {/* --- RENDER SECTIONS --- */}
                                    {category.sections?.map((section, sectionIdx) => {
                                        if (!section) return null; // SAFETY CHECK: Prevents "undefined reading id"
                                        return (
                                            <div key={section.id || sectionIdx} className="w-full">
                                                {/* Section Item */}
                                                <SidebarItem
                                                    id={section.id}
                                                    name={section.title}
                                                    activeSection={activeSection}
                                                    handleNavigate={handleNavigate}
                                                    openDropdown={openDropdown}
                                                    toggleDropdown={toggleDropdown}
                                                    isLastItem={
                                                        sectionIdx === category.sections.length - 1 &&
                                                        !section.subsections?.length &&
                                                        !section.optionalSubsections?.length &&
                                                        !section.deletedRequiredSubsections?.length
                                                    }
                                                    canDelete={section.canDelete}
                                                    onDelete={(id) => {
                                                        if (section.canDelete && onDeleteSection) onDeleteSection(category.id, id);
                                                    }}
                                                    onRename={(id) => console.log('Rename Section', id)}
                                                    onMove={(itemId, direction) => {
                                                        if (onMoveSection) onMoveSection(category.id, itemId, direction);
                                                    }}
                                                    onPreview={(id) => { if (onPreviewSection) onPreviewSection(id); }}
                                                    onSampleContent={(id) => { if (onSampleContent) onSampleContent(id); }}
                                                    canMoveUp={sectionIdx > 0}
                                                    canMoveDown={sectionIdx < category.sections.length - 1}
                                                />

                                                {/* --- SUBSECTIONS CONTAINER --- */}
                                                <div className="ml-4 mt-2 flex flex-col gap-2">
                                                    
                                                    {/* 1. Visible Subsections */}
                                                    {section.subsections?.map((subsection, subIdx) => {
                                                        if (!subsection) return null; // SAFETY CHECK
                                                        return (
                                                            <SidebarItem
                                                                key={subsection.id || subIdx}
                                                                id={subsection.id}
                                                                name={subsection.title}
                                                                activeSection={activeSection}
                                                                handleNavigate={handleNavigate}
                                                                openDropdown={openDropdown}
                                                                toggleDropdown={toggleDropdown}
                                                                isLastItem={subIdx === (section.subsections.length - 1)}
                                                                isSubsection={true}
                                                                canDelete={subsection.canDelete}
                                                                onDelete={(id) => {
                                                                    if (subsection.canDelete && onDeleteSubsection) {
                                                                        onDeleteSubsection(category.id, section.id, id);
                                                                    }
                                                                }}
                                                                onRename={(id) => console.log('Rename Subsection', id)}
                                                                onMove={(itemId, direction) => {
                                                                    if (onMoveSubsection) {
                                                                        onMoveSubsection(category.id, section.id, itemId, direction);
                                                                    }
                                                                }}
                                                                onPreview={(id) => { if (onPreviewSection) onPreviewSection(id); }}
                                                                onSampleContent={(id) => { if (onSampleContent) onSampleContent(id); }}
                                                                canMoveUp={subIdx > 0}
                                                                canMoveDown={subIdx < section.subsections.length - 1}
                                                            />
                                                        );
                                                    })}

                                                    {/* 2. Restore Required Subsections (Red) */}
                                                    {section.deletedRequiredSubsections?.length > 0 && (
                                                        <div className="w-full pt-1 border-t border-lightGrey/50 mt-1">
                                                            <span className="text-[10px] text-red-500 font-bold px-2 uppercase tracking-wider">Restore Subsection</span>
                                                            {section.deletedRequiredSubsections.map((reqSub, idx) => (
                                                                <OptionalSectionItem
                                                                    key={reqSub.id || idx}
                                                                    id={reqSub.id}
                                                                    name={reqSub.title}
                                                                    isRestore={true}
                                                                    onAdd={(subId) => {
                                                                        if (onAddSubsection) onAddSubsection(category.id, section.id, subId);
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 3. Add Optional Subsections (Purple) - gated by canUseTemplates */}
                                                    {canUseTemplates && section.optionalSubsections?.length > 0 && (
                                                        <div className="w-full pt-1 border-t border-lightGrey/50 mt-1">
                                                            <span className="text-[10px] text-purple font-bold px-2 uppercase tracking-wider">Add Subsection</span>
                                                            {section.optionalSubsections.map((optSub, idx) => (
                                                                <OptionalSectionItem
                                                                    key={optSub.id || idx}
                                                                    id={optSub.id}
                                                                    name={optSub.title}
                                                                    onAdd={(subId) => {
                                                                        if (onAddSubsection) onAddSubsection(category.id, section.id, subId);
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* --- ADD REQUIRED SECTIONS AREA --- */}
                                    {category.deletedRequiredSections?.length > 0 && (
                                        <div className="w-full mt-4 pt-3 border-t border-lightGrey">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Plus className="w-3.5 h-3.5 text-red-500" />
                                                <span className="text-sm font-medium text-red-500">Restore Required</span>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                {category.deletedRequiredSections.map((reqSection, reqIdx) => (
                                                    <OptionalSectionItem
                                                        key={reqSection.id || reqIdx}
                                                        id={reqSection.id}
                                                        name={reqSection.title}
                                                        isRestore={true}
                                                        onAdd={(sectionId) => {
                                                            if (onAddSection) onAddSection(category.id, sectionId);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* --- ADD OPTIONAL SECTIONS AREA --- gated by canUseTemplates */}
                                    {canUseTemplates && category.optionalSections?.length > 0 && (
                                        <div className="w-full mt-4 pt-3 border-t border-lightGrey">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Plus className="w-3.5 h-3.5 text-purple" />
                                                <span className="text-sm font-medium text-purple">Add Sections</span>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                {category.optionalSections.map((optSection, optIdx) => (
                                                    <OptionalSectionItem
                                                        key={optSection.id || optIdx}
                                                        id={optSection.id}
                                                        name={optSection.title}
                                                        onAdd={(sectionId) => {
                                                            if (onAddSection) onAddSection(category.id, sectionId);
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })
                : 
                // Fallback for legacy flat sections (no category wrapper)
                sections.map((section, sectionIdx) => {
                    if (!section) return null;
                    const subsections = section.subsections || section.pages || [];
                    const isLastSection =
                        sectionIdx === sections.length - 1 && !subsections.length;

                    return (
                        <div
                            key={section.id || sectionIdx}
                            className="flex flex-col w-full border-b border-lightGrey px-[25px] py-[18px] gap-2"
                        >
                            <SidebarItem
                                id={section.id}
                                name={section.title}
                                activeSection={activeSection}
                                handleNavigate={handleNavigate}
                                openDropdown={openDropdown}
                                toggleDropdown={toggleDropdown}
                                isLastItem={isLastSection}
                                canDelete={section.canDelete}
                                onDelete={(id) => console.log('Delete Section', id)}
                                onRename={(id) => console.log('Rename Section', id)}
                                onMove={(itemId, direction) => console.log('Move Section', itemId, direction)}
                                onPreview={(id) => {
                                    if (onPreviewSection) onPreviewSection(id);
                                }}
                                onSampleContent={(id) => {
                                    if (onSampleContent) onSampleContent(id);
                                }}
                                canMoveUp={sectionIdx > 0}
                                canMoveDown={sectionIdx < sections.length - 1}
                            />

                            {subsections.length > 0 && (
                                <div className="ml-4 flex flex-col gap-2">
                                    {subsections.map((subsection, subIdx) => {
                                        if (!subsection) return null;
                                        return (
                                            <SidebarItem
                                                key={subsection.id || subIdx}
                                                id={subsection.id}
                                                name={subsection.title || subsection.name}
                                                activeSection={activeSection}
                                                handleNavigate={handleNavigate}
                                                openDropdown={openDropdown}
                                                toggleDropdown={toggleDropdown}
                                                isLastItem={
                                                    sectionIdx === sections.length - 1 &&
                                                    subIdx === subsections.length - 1
                                                }
                                                isSubsection={true}
                                                canDelete={subsection.canDelete}
                                                onDelete={(id) => console.log('Delete Subsection', id)}
                                                onRename={(id) => console.log('Rename Subsection', id)}
                                                onMove={(itemId, direction) =>
                                                    console.log('Move Subsection', itemId, direction)
                                                }
                                                onPreview={(id) => {
                                                    if (onPreviewSection) onPreviewSection(id);
                                                }}
                                                onSampleContent={(id) => {
                                                    if (onSampleContent) onSampleContent(id);
                                                }}
                                                canMoveUp={subIdx > 0}
                                                canMoveDown={subIdx < subsections.length - 1}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })
            }
        </div>
    );
}